import { X } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Card, CardContent } from "@/components/ui/card";
import { ClosedMonthsDatePicker } from "@/components/impacto/ClosedMonthsDatePicker";
import { SearchableGeneratorSelect } from "@/components/dashboard/SearchableGeneratorSelect";
import { useAuthContext } from "@/components/AuthProvider";
import type { ImpactFiltersJadlog as ImpactFiltersJadlogType } from "@/hooks/useImpactDataJadlog";

interface ImpactFiltersJadlogProps {
  filters: ImpactFiltersJadlogType;
  onFiltersChange: (filters: Partial<ImpactFiltersJadlogType>) => void;
  onClearFilters: () => void;
  dateShortcuts: Array<{
    label: string;
    getValue: () => {
      from: Date;
      to: Date;
    };
  }>;
  closedMonths: Date[];
  establishmentOptions?: Array<{
    value: string;
    label: string;
  }>;
  wasteTypeOptions?: Array<{
    value: string;
    label: string;
  }>;
}

export const ImpactFiltersJadlog = ({
  filters,
  onFiltersChange,
  onClearFilters,
  dateShortcuts,
  closedMonths,
  establishmentOptions = [],
  wasteTypeOptions = [],
}: ImpactFiltersJadlogProps) => {
  const { profile } = useAuthContext();
  const isInternal = profile?.is_internal === true;

  // Verificar se há filtros ativos (apenas material agora)
  const hasActiveFilters = filters.wasteType !== "all";

  return (
    <Card className="bg-primary-50 border-border">
      <CardContent className="p-4">
        {/* Header row */}
        <div className="flex items-center justify-between mb-3">
          <h2 className="text-base font-semibold text-[#10261F]">Filtros</h2>
          {hasActiveFilters && (
            <Button
              variant="ghost"
              size="sm"
              onClick={onClearFilters}
              className="text-muted-foreground hover:text-foreground gap-1.5"
            >
              <X className="h-4 w-4" />
              Limpar filtros
            </Button>
          )}
        </div>

        <div className="flex flex-wrap items-start gap-6">
          {/* Period Filter - Apenas meses fechados */}
          <div className="flex flex-col gap-1.5">
            <label className="text-sm text-muted-foreground">
              Período (meses fechados)
            </label>
            <ClosedMonthsDatePicker
              value={{
                start: filters.dateRange.from,
                end: filters.dateRange.to,
              }}
              onChange={(range) => {
                if (range) {
                  onFiltersChange({
                    dateRange: {
                      from: range.start,
                      to: range.end,
                    },
                  });
                }
              }}
              closedMonths={closedMonths}
              dateShortcuts={dateShortcuts}
              className="min-w-[200px]"
            />
          </div>

          {/* Material Filter */}
          <div className="flex flex-col gap-1.5">
            <label className="text-sm text-muted-foreground">Material</label>
            <Select
              value={filters.wasteType}
              onValueChange={(value) => onFiltersChange({ wasteType: value })}
            >
              <SelectTrigger className="w-[180px] h-10">
                <SelectValue placeholder="TOTAL" />
              </SelectTrigger>
              <SelectContent className="bg-background z-50 max-h-[300px]">
                <SelectItem value="all">TOTAL</SelectItem>
                {wasteTypeOptions.map((option) => (
                  <SelectItem key={option.value} value={option.value}>
                    {option.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};
